import { useMemo } from 'react';
import { css } from '@emotion/react';
import { InvoiceWithPayoutDescriptor } from '@frontend/api-invoices';
import { PrintDialog } from '@frontend/assets';
import { useTranslation } from '@frontend/i18n';
import { useLocationSurchargeState } from '@frontend/payments-hooks';
import { useQueryAllInvoices } from '@frontend/payments-invoice-controller';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import {
  GlobalSearchConfig,
  PaginationProps,
  SortingValue,
  Table,
  TableActionsProps,
  TableRowActions,
  ManualFilters,
} from '@frontend/design-system';
import { useColumnsWithPayoutDescriptor } from '../../../hooks/table/use-columns-with-payout-descriptor';
import { useCareCreditAccountLastFourMap } from '../../../hooks/use-care-credit-account-last-four-map';
import { PaymentsTableInstances } from '../../../utils/helpers';
import { PrintHeader } from '../../PrintHeader';
import { generateColumns } from './generate-columns';

interface PaymentRequestsListTableProps {
  locationIds: string[];
  paymentRequestsList: InvoiceWithPayoutDescriptor[];
  tableFilter: ManualFilters<InvoiceWithPayoutDescriptor>;
  loading: boolean;
  onSortChange: (res: SortingValue<string>[]) => void;
  tableActions: TableActionsProps<InvoiceWithPayoutDescriptor>;
  rowActions: TableRowActions<InvoiceWithPayoutDescriptor>;
  searchConfig: GlobalSearchConfig;
  paginationConfig?: PaginationProps;
}

export const PaymentRequestsListTable = ({
  locationIds,
  paymentRequestsList,
  loading,
  onSortChange,
  tableFilter,
  rowActions,
  tableActions,
  searchConfig,
  paginationConfig,
}: PaymentRequestsListTableProps) => {
  const { t } = useTranslation('payments');

  const { isAnyEnabled: hasSurchargesEnabled } = useLocationSurchargeState(locationIds);
  const careCreditLastFourMap = useCareCreditAccountLastFourMap(paymentRequestsList);
  const generatePaymentRequestColumns = useMemo(
    () => (options: Parameters<typeof generateColumns>[0]) =>
      generateColumns({
        ...options,
        careCreditLastFourMap,
      }),
    [careCreditLastFourMap]
  );

  const { columns: paymentRequestsTableColumnConfig } = useColumnsWithPayoutDescriptor<InvoiceWithPayoutDescriptor>({
    forPrint: false,
    generateColumns: generatePaymentRequestColumns,
    hideLocationNameColumn: locationIds.length === 1,
    tableName: PaymentsTableInstances.PaymentRequests,
    hasSurchargesEnabled,
  });

  return (
    <Table
      colConfig={paymentRequestsTableColumnConfig}
      data={paymentRequestsList}
      isLoading={loading}
      hasFilterColumns
      hasResizeColumns
      hasResponsiveColWidths
      hasGlobalSearch
      globalSearchConfig={searchConfig}
      manualSortBy
      onSortChange={onSortChange}
      manualFilters
      manualFiltersRender={tableFilter}
      rowActions={rowActions}
      tableActions={tableActions}
      isPaginated
      manualPaginationConfig={paginationConfig}
      globalTrackingId='payments-paymentrequests-list-table'
      tableInstanceId={PaymentsTableInstances.PaymentRequests}
      emptyStateConfig={{
        type: 'payments',
        header: t('No data to display'),
      }}
    />
  );
};

const printStyles = {
  marginBottom: css`
    margin-bottom: ${theme.spacing(3)};
  `,
};

export type PrintRequestListTableProps = {
  onClose: () => void;
  headerContent?: React.ReactNode;
};

export const PrintRequestListTable = ({ onClose, headerContent }: PrintRequestListTableProps) => {
  const { selectedLocationIds: locationIds } = useAppScopeStore();
  const { isAnyEnabled: hasSurchargesEnabled } = useLocationSurchargeState(locationIds);
  const { invoices } = useQueryAllInvoices();
  const careCreditLastFourMap = useCareCreditAccountLastFourMap(invoices ?? []);
  const generatePaymentRequestColumns = useMemo(
    () => (options: Parameters<typeof generateColumns>[0]) =>
      generateColumns({
        ...options,
        careCreditLastFourMap,
      }),
    [careCreditLastFourMap]
  );

  const { columns: paymentRequestsTableColumnConfig } = useColumnsWithPayoutDescriptor<InvoiceWithPayoutDescriptor>({
    forPrint: false,
    generateColumns: generatePaymentRequestColumns,
    hasSurchargesEnabled,
    tableName: PaymentsTableInstances.PaymentRequests,
  });

  if (!invoices || invoices.length === 0) {
    return null; // No invoices to display
  }

  return (
    <PrintDialog show={true} onClose={onClose} landscape>
      <div
        css={css`
          margin: ${theme.spacing(2)};
          @media print {
            zoom: 95%;
          }
        `}
      >
        <PrintHeader css={printStyles.marginBottom} />
        {headerContent}
        <Table
          wrapperStyle={css`
            height: fit-content;
            width: 100%;
          `}
          colConfig={paymentRequestsTableColumnConfig}
          data={invoices}
          tableInstanceId={PaymentsTableInstances.PaymentRequests}
        />
      </div>
    </PrintDialog>
  );
};
